<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'

// The beforeinstallprompt event is non-standard but supported in Chromium browsers.
// Firefox and Safari do not fire it — iOS requires manual "Add to Home Screen".
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
  prompt(): Promise<void>
}

const DISMISS_KEY = 'riffdraft-install-dismissed-at'
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null)
const showBanner = ref(false)
const showIosInstructions = ref(false)

// Detect iOS Safari (not Chrome/Firefox on iOS, though those use WebKit too —
// the Add to Home Screen flow is the same on all iOS browsers).
const isIos = computed(() => {
  const ua = window.navigator.userAgent
  return /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream
})

// Detect if already running as installed PWA
const isStandalone = computed(() => {
  return window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true
})

const wasRecentlyDismissed = () => {
  const dismissedAt = localStorage.getItem(DISMISS_KEY)
  if (!dismissedAt) return false
  const elapsed = Date.now() - parseInt(dismissedAt, 10)
  return elapsed < DISMISS_DURATION_MS
}

const handleBeforeInstallPrompt = (e: Event) => {
  e.preventDefault()
  deferredPrompt.value = e as BeforeInstallPromptEvent
  if (!wasRecentlyDismissed()) {
    showBanner.value = true
  }
}

const handleAppInstalled = () => {
  showBanner.value = false
  deferredPrompt.value = null
  localStorage.removeItem(DISMISS_KEY)
}

const install = async () => {
  if (!deferredPrompt.value) return
  await deferredPrompt.value.prompt()
  const { outcome } = await deferredPrompt.value.userChoice
  if (outcome === 'dismissed') {
    localStorage.setItem(DISMISS_KEY, Date.now().toString())
  }
  deferredPrompt.value = null
  showBanner.value = false
}

const dismiss = () => {
  localStorage.setItem(DISMISS_KEY, Date.now().toString())
  showBanner.value = false
}

const showIosPrompt = () => {
  showIosInstructions.value = true
}

const dismissIos = () => {
  localStorage.setItem(DISMISS_KEY, Date.now().toString())
  showIosInstructions.value = false
}

onMounted(() => {
  if (isStandalone.value) return

  window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
  window.addEventListener('appinstalled', handleAppInstalled)

  // iOS does not fire beforeinstallprompt — show the banner after a delay
  // so users have time to land on the app first.
  if (isIos.value && !wasRecentlyDismissed()) {
    setTimeout(() => {
      if (!isStandalone.value) showBanner.value = true
    }, 3000)
  }
})

onUnmounted(() => {
  window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
  window.removeEventListener('appinstalled', handleAppInstalled)
})
</script>

<template>
  <Transition name="slide-up">
    <div
      v-if="showBanner && !isStandalone"
      class="fixed bottom-4 left-4 right-4 z-50 sm:left-auto sm:right-4 sm:max-w-sm"
    >
      <div class="rd-card p-4 shadow-lg flex items-start gap-3">
        <img src="/riffdraft-icon.svg" alt="" class="w-12 h-12 flex-shrink-0" />
        <div class="flex-1 min-w-0">
          <h3 class="text-sm font-bold text-slate-900">Install Riff Draft</h3>
          <p class="text-xs text-slate-600 mt-0.5">
            Add to your home screen for a faster, app-like experience.
          </p>
          <div class="flex gap-2 mt-3">
            <Button
              v-if="isIos"
              label="How to Install"
              size="small"
              severity="success"
              class="text-xs"
              @click="showIosPrompt"
            />
            <Button
              v-else
              label="Install"
              icon="pi pi-download"
              size="small"
              severity="success"
              class="text-xs"
              @click="install"
            />
            <Button
              label="Not Now"
              size="small"
              severity="secondary"
              text
              class="text-xs"
              @click="dismiss"
            />
          </div>
        </div>
      </div>
    </div>
  </Transition>

  <Dialog
    v-model:visible="showIosInstructions"
    modal
    header="Install Riff Draft"
    :style="{ width: '90vw', maxWidth: '420px' }"
    @hide="dismissIos"
  >
    <div class="space-y-4 text-sm">
      <p class="text-slate-700">
        To install Riff Draft on your iPhone or iPad:
      </p>
      <ol class="space-y-3 pl-1">
        <li class="flex gap-3">
          <span class="flex-shrink-0 w-6 h-6 rounded-full bg-teal-500 text-white flex items-center justify-center text-xs font-bold">1</span>
          <span class="text-slate-700">
            Tap the <strong>Share</strong> button
            <i class="pi pi-upload text-teal-600 mx-1"></i>
            at the bottom of Safari
          </span>
        </li>
        <li class="flex gap-3">
          <span class="flex-shrink-0 w-6 h-6 rounded-full bg-teal-500 text-white flex items-center justify-center text-xs font-bold">2</span>
          <span class="text-slate-700">
            Scroll down and tap <strong>Add to Home Screen</strong>
          </span>
        </li>
        <li class="flex gap-3">
          <span class="flex-shrink-0 w-6 h-6 rounded-full bg-teal-500 text-white flex items-center justify-center text-xs font-bold">3</span>
          <span class="text-slate-700">
            Tap <strong>Add</strong> in the top right corner
          </span>
        </li>
      </ol>
      <p class="text-xs text-slate-500 pt-2 border-t border-slate-200">
        Note: Riff Draft must be opened in Safari for this to work — not another browser.
      </p>
    </div>
  </Dialog>
</template>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(120%);
  opacity: 0;
}
</style>
